import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import './drawer.css';

/**
 * Painel lateral para formulários longos.
 *
 * `<dialog>` nativo pelas mesmas razões do modal: foco preso, Esc, camada
 * superior e conteúdo de trás inerte, tudo entregue pelo navegador.
 *
 * Diferente do modal, aqui cabeçalho e rodapé são fixos e só o miolo rola.
 * Num formulário de quinze campos isso é a diferença entre saber e não saber
 * o que você está editando depois de rolar três telas.
 */
export function Drawer({
  open, onClose, title, description, children, footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = `drw-${title.replace(/\W+/g, '-').toLowerCase()}`;

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (open && !node.open) node.showModal();
    if (!open && node.open) node.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="drawer"
      aria-labelledby={titleId}
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClose={onClose}
    >
      <div className="drawer__caixa">
        <header className="drawer__topo">
          <div>
            <h2 className="drawer__titulo" id={titleId}>{title}</h2>
            {description && <p className="drawer__sub">{description}</p>}
          </div>
          {/* Ícone de verdade, não o caractere "✕". O caractere herda a métrica
              da fonte, não centraliza no botão e é lido em voz alta como
              "multiplicação" por alguns leitores de tela. */}
          <button type="button" className="drawer__fechar" onClick={onClose} aria-label={`Fechar ${title}`}>
            <X size={20} aria-hidden="true" />
          </button>
        </header>

        <div className="drawer__corpo">
          <div className="drawer__medida">{children}</div>
        </div>

        {footer && <div className="drawer__rodape">{footer}</div>}
      </div>
    </dialog>
  );
}
